'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import { Toaster, toast } from 'sonner';

// Mock credentials — backend integration: replace with real auth (NextAuth / JWT / session)
const MOCK_CREDENTIALS = {
  email: 'david@portionsforfoxes.net',
  password: 'Portions1986',
};

type LoginFormValues = {
  email: string;
  password: string;
};

export default function AdminLoginPage() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({ mode: 'onBlur' });

  async function onSubmit(data: LoginFormValues) {
    setAuthError('');
    setLoading(true);

    // Backend integration: replace with real auth call
    await new Promise((r) => setTimeout(r, 1200));

    if (
      data.email.trim().toLowerCase() === MOCK_CREDENTIALS.email &&
      data.password === MOCK_CREDENTIALS.password
    ) {
      toast.success('Welcome back!', { description: 'Redirecting to admin dashboard…' });
      setTimeout(() => router.push('/admin-sign-up-management'), 800);
    } else {
      setLoading(false);
      setAuthError('Invalid credentials. Please try again.');
    }
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f] flex">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            color: '#f0ece3',
            fontFamily: 'DM Sans, sans-serif',
          },
        }}
      />

      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative overflow-hidden flex-col justify-between p-12 bg-[#0a0a0a] border-r border-[#1e1e1e]">
        {/* Grid texture */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 48px, #f5c842 48px, #f5c842 49px), repeating-linear-gradient(90deg, transparent, transparent 48px, #f5c842 48px, #f5c842 49px)',
          }}
        />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#f5c842]/40 to-transparent" />

        <div className="relative">
          <Link href="/musician-sign-up" className="flex items-center gap-3">
            <AppLogo size={40} />
            <span className="font-display text-3xl text-[#f5c842] tracking-widest">OPENMIC</span>
          </Link>
        </div>

        <div className="relative space-y-6">
          <div>
            <h1 className="font-display text-7xl xl:text-8xl text-[#f0ece3] leading-none">
              ADMIN
              <br />
              <span className="text-stroke-yellow">ACCESS</span>
            </h1>
            <p className="text-[#666] text-base mt-4 max-w-xs leading-relaxed">
              Manage sign-ups, confirm acts, and keep the night running smoothly.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {['View all sign-ups', 'Confirm acts', 'Remove entries', 'Filter by type'].map((feat) => (
              <span
                key={`feat-${feat}`}
                className="flex items-center gap-1.5 bg-[#1a1a1a] border border-[#2a2a2a] text-[#888] text-xs font-medium px-3 py-1.5 rounded-full"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#f5c842]" />
                {feat}
              </span>
            ))}
          </div>
        </div>

        <div className="relative">
          <p className="text-[#333] text-xs">
            OpenMic · Grassroots music, no corporate nonsense.
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 sm:px-10 lg:px-16 xl:px-20">
        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-10 lg:hidden">
          <AppLogo size={32} />
          <span className="font-display text-2xl text-[#f5c842] tracking-widest">OPENMIC</span>
        </div>

        <div className="w-full max-w-sm">
          <h2 className="font-display text-4xl text-[#f0ece3] tracking-wider mb-1">SIGN IN</h2>
          <p className="text-[#666] text-sm mb-8">Admin access only.</p>

          {authError && (
            <div className="bg-[#e8386d]/10 border border-[#e8386d]/30 rounded-lg px-4 py-3 mb-6 flex items-start gap-2">
              <span className="text-[#e8386d] text-xs mt-0.5">⚠</span>
              <p className="text-[#e8386d] text-sm">{authError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="label-text">Email address</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={`input-dark ${errors.email ? 'border-[#e8386d]' : ''}`}
                placeholder="organiser@openmic.co.uk"
                {...register('email', {
                  required: 'Email is required.',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email.' },
                })}
              />
              {errors.email && <p className="error-text">{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="label-text">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className={`input-dark pr-10 ${errors.password ? 'border-[#e8386d]' : ''}`}
                  placeholder="Your password"
                  {...register('password', { required: 'Password is required.' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-[#f0ece3] transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className="error-text">{errors.password.message}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 mt-2"
              style={{ minHeight: '48px' }}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Signing in…</span>
                </>
              ) : (
                <span>Sign In</span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}